"use client";
import { DirectusImage } from "@/components/DirectusImage";
import RouterLink from "@/components/ui/router-link";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { useI18nText, type I18nText } from "@/hooks/useI18nText";
import { Fragment, useRef, useState, useLayoutEffect, useMemo } from "react";
import { cn } from "@/lib/utils";

const DEFAULT_HEIGHT = 650;

export default function CommonBanner(props: {
  className?: string;
  data?: {
    height?: number;
    image?: string;
    video?: string;
    title?: I18nText;
    subTitle?: I18nText;
    subTitleSize?: "default" | "sm";
    textColor?: string;
    textAlign?: "left" | "center" | "right";
    mainButton?: {
      id?: string;
      text?: I18nText;
      href?: string;
      icon?: string;
    };
    secondButton?: {
      id?: string;
      text?: I18nText;
      href?: string;
      icon?: string;
    };
    anchors?: {
      text?: I18nText;
      blockId?: string;
      href?: string;
    }[];
  };
}) {
  const { translation } = useI18nText();
  const { anchors } = props.data ?? {};
  const [activeAnchor, setActiveAnchor] = useState<string | undefined>();
  const activeAnchorName = useMemo(() => {
    const text = anchors?.find((el) => el.blockId === activeAnchor)?.text ?? "";
    return translation(text);
  }, [anchors, activeAnchor, translation]);
  const [fixed, setFixed] = useState(false);
  const bannerBox = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const fixedObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setFixed(false);
      } else {
        setFixed(true);
      }
    });
    const anchorDoms = anchors?.map((el) => {
      if (el.blockId) {
        return document.getElementById(el.blockId);
      }
      return null;
    });
    const anchorObserver = new IntersectionObserver((entries) => {
      for (let i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          setActiveAnchor(entries[i].target.id);
        }
      }
    });
    if (bannerBox.current) {
      fixedObserver.observe(bannerBox.current);
    }

    anchorDoms?.forEach((el) => {
      if (el) {
        anchorObserver.observe(el);
      }
    });

    return () => {
      if (bannerBox.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        fixedObserver.unobserve(bannerBox.current);
      }
      anchorDoms?.forEach((el) => {
        if (el) {
          anchorObserver.unobserve(el);
        }
      });
    };
  });

  return (
    <div
      ref={bannerBox}
      className={cn(
        "relative flex w-full flex-col justify-center max-md:!h-[60vw]",
        props.className
      )}
      style={{
        height: props.data?.height ?? DEFAULT_HEIGHT,
      }}>
      <div
        className={cn(
          "relative z-10 mx-auto w-full max-xl:px-10 max-lg:px-6 xl:w-[1200px]",
          {
            "flex flex-col items-center": props.data?.textAlign === "center",
            "flex flex-col items-end": props.data?.textAlign === "right",
          }
        )}
        style={{
          color: props.data?.textColor,
          textAlign: props.data?.textAlign,
        }}>
        {props.data?.title && (
          <h1
            className={cn(
              "whitespace-pre-line text-2xl font-bold md:max-w-[66%] md:text-3xl md:leading-snug xl:text-5xl en:xl:text-4xl",
              {
                "xl:text-[40px] en:xl:text-[40px]":
                  props.data?.subTitleSize === "sm",
              }
            )}>
            {translation(props.data.title || "")}
          </h1>
        )}
        {props.data?.subTitle && (
          <p
            className={cn(
              "mt-1 max-w-[66%] whitespace-pre-line text-sm md:mt-6 md:text-2xl en:md:text-xl",
              {
                "md:text-base en:md:text-base":
                  props.data?.subTitleSize === "sm",
              }
            )}>
            {translation(props.data.subTitle || "")}
          </p>
        )}
        {!anchors?.length &&
          (props.data?.mainButton?.text || props.data?.secondButton?.text) && (
            <div className="mt-5 flex flex-col gap-y-4 md:mt-14 md:flex-row md:gap-x-10">
              {props.data?.mainButton?.text && (
                <RouterLink
                  id={props.data.mainButton?.id}
                  href={props.data.mainButton.href}>
                  <Button className="h-auto rounded-3xl px-4 py-1 text-xs md:rounded-md md:px-10 md:py-3 md:text-lg">
                    {props.data.mainButton.icon && (
                      <img
                        src={`${props.data.mainButton.icon}`}
                        alt={translation(props.data.mainButton.text || "")}
                        className="mr-1 h-4 w-4 flex-shrink-0 md:h-6 md:w-6"
                      />
                    )}
                    {translation(props.data.mainButton.text || "")}
                  </Button>
                </RouterLink>
              )}
              {props.data?.secondButton?.text && (
                <RouterLink href={props.data.secondButton.href}>
                  <Button
                    className="h-auto rounded-3xl px-4 py-1 text-xs md:rounded-md md:px-10 md:py-3 md:text-lg"
                    variant="outline">
                    {props.data.secondButton.icon && (
                      <img
                        src={`${props.data.secondButton.icon}`}
                        alt={translation(props.data.secondButton.text || "")}
                        className="mr-1 h-4 w-4 flex-shrink-0 md:h-6 md:w-6"
                      />
                    )}
                    {translation(props.data.secondButton.text || "")}
                  </Button>
                </RouterLink>
              )}
            </div>
          )}
      </div>
      {!!anchors?.length && (
        <div
          className={cn(
            "absolute -bottom-6 z-20 mt-0 h-12 w-full duration-300 max-md:px-6 md:-bottom-12 md:h-24",
            {
              "fixed top-0 mt-11 md:mt-[3.75rem]": fixed,
            }
          )}>
          <div className="mx-auto flex h-full w-full items-center justify-between rounded-lg bg-white px-3 shadow-lg md:px-7 xl:w-[1200px]">
            <div className="lg:gapx-8 hidden items-center md:flex md:gap-x-4 xl:gap-x-12">
              {anchors.map((el, index) => (
                <Fragment key={index}>
                  {index !== 0 && <hr className="h-8 w-0 border-l"></hr>}
                  {el.href ? (
                    <RouterLink className="text-2xl" href={el.href}>
                      {translation(el.text || "")}
                    </RouterLink>
                  ) : (
                    <span
                      onClick={() => {
                        if (el.blockId) {
                          const dom = document.getElementById(el.blockId);
                          if (dom) {
                            dom.scrollIntoView();
                          }
                        }
                      }}
                      className={cn(
                        "cursor-pointer select-none text-2xl hover:text-primary hover:opacity-80",
                        activeAnchor === el.blockId && "text-primary"
                      )}>
                      {translation(el.text || "")}
                    </span>
                  )}
                </Fragment>
              ))}
            </div>
            <Select
              onValueChange={(val) => {
                if (val && !val.startsWith("ANCHOR_LINK")) {
                  const dom = document.getElementById(val);
                  if (dom) {
                    dom.scrollIntoView();
                  }
                }
              }}
              value={activeAnchor}>
              <SelectTrigger className="w-auto border-none text-primary md:hidden">
                {activeAnchorName}
              </SelectTrigger>
              <SelectContent className="border border-[#E5E5E5] bg-white">
                {anchors.map((el, index) => {
                  if (!el.href) {
                    return (
                      <SelectItem
                        className={cn(
                          "flex w-full items-center p-1 text-sm outline-none",
                          {
                            "text-primary": el.blockId === activeAnchor,
                          }
                        )}
                        key={`ANCHOR_${index}`}
                        indicator={false}
                        value={el.blockId || `ANCHOR_LINK_${index}`}>
                        {translation(el.text ?? "")}
                      </SelectItem>
                    );
                  }
                  return (
                    <RouterLink
                      className={cn(
                        "flex w-full items-center p-1 text-sm outline-none",
                        {
                          "text-primary": el.blockId === activeAnchor,
                        }
                      )}
                      key={`ANCHOR_${index}`}
                      href={el.href}>
                      {translation(el.text ?? "")}
                    </RouterLink>
                  );
                })}
              </SelectContent>
            </Select>
            <div></div>
            {(props.data?.mainButton || props.data?.secondButton) && (
              <div className="flex gap-x-4">
                {props.data?.mainButton && (
                  <RouterLink href={props.data.mainButton.href}>
                    <Button className="h-auto rounded-xl px-5 py-1 text-xs md:rounded-[4px] md:px-9 md:py-3 md:text-lg">
                      {translation(props.data.mainButton.text || "")}
                    </Button>
                  </RouterLink>
                )}
                {props.data?.secondButton && (
                  <RouterLink href={props.data.secondButton.href}>
                    <Button
                      className="h-auto rounded-xl px-5 py-1 text-xs md:rounded-[4px] md:px-9 md:py-3 md:text-lg"
                      variant="outline">
                      {translation(props.data.secondButton.text || "")}
                    </Button>
                  </RouterLink>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {!props.data?.video && props.data?.image && (
        <DirectusImage
          fill
          sizes="100vw"
          id={props.data?.image}
          priority
          className="object-cover object-center"
          alt={translation(props.data?.title || "")}
        />
      )}
      {/** 移动端只显示图片 */}
      {props.data?.image && (
        <DirectusImage
          fill
          sizes="100vw"
          id={props.data?.image}
          priority
          className="object-cover md:hidden"
          alt={translation(props.data?.title || "")}
        />
      )}
      {/** 配置了视频时，非移动端显示视频 */}
      {props.data?.video && (
        <video
          muted
          autoPlay
          loop
          src={`${process.env.NEXT_PUBLIC_ASSETS}/${props.data?.video}`}
          poster={
            props.data?.image
              ? `${process.env.NEXT_PUBLIC_ASSETS}/${props.data?.image}?transforms=[["blur",70]]`
              : undefined
          }
          className="absolute h-full w-full object-cover max-md:hidden"></video>
      )}
    </div>
  );
}
